import { useCallback } from 'react';
import clsx from 'clsx';
import { Checkbox } from '@headlessui/react';

import { GroupedOption, SelectOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { VolumesAutoCompleteResponse } from '~/shared/types/api';
import Typography from '~/shared/components/Typography';
import CheckIcon from '~/shared/assets/icons/check.svg';

import { useSearchFilter } from './common/useSearchFilter';
import SearchFilterBase from './common/SearchFilterBase';

export default function SearchVolumeId({
  filters,
  setFilters,
  isDisclosureOpen,
  accounts,
}: {
  filters: SelectOption[];
  setFilters: (selections: SelectOption[]) => void;
  isDisclosureOpen?: boolean;
  accounts?: string;
}) {
  const api = useXFetch();

  const searchOptions = useCallback(
    async (pattern: string): Promise<GroupedOption[]> => {
      const trimmedPattern = pattern.trim();

      if (!trimmedPattern) {
        return [];
      }

      try {
        const params = new URLSearchParams({ field: 'volumeId', pattern: trimmedPattern });
        if (accounts) params.set('accounts', accounts);
        const qs = params.toString();

        const response = await api<VolumesAutoCompleteResponse>(
          `/api/v1/volumes/autocomplete?${qs}`,
          { cache: 'no-store' }
        );
        const volumeIds = response.values || [];

        const groupedResults: GroupedOption[] =
          volumeIds.length > 0
            ? [
                {
                  label: 'Volume ID',
                  options: volumeIds.map((id) => ({
                    id,
                    name: id,
                    group: 'volumeId' as const,
                  })),
                },
              ]
            : [];

        return groupedResults;
      } catch (error) {
        console.error('[VolumeIdFilter] Error searching volume IDs:', error);
        return [];
      }
    },
    [api, accounts]
  );

  const {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    selectedIdSet,
    flattenedResults,
    inputRef,
    resultsRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  } = useSearchFilter({
    initialSelections: filters,
    onSelectionsChange: setFilters,
    searchFunction: searchOptions,
    filterGroup: 'volumeId',
    debounceMs: 200,
    defaultQuery: 'vol-',
    isDisclosureOpen,
  });

  const renderOption = useCallback(
    (option: SelectOption, isSelected: boolean, isFocused: boolean = false) => (
      <div
        key={option.id}
        className={clsx(
          'p-3 my-1 rounded-lg transition-colors flex items-center',
          isFocused && 'bg-primary-20'
        )}
        onClick={() => handleSelect(option)}
      >
        <div className="mr-2">
          <Checkbox
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(option);
            }}
            className={clsx(
              'group block size-5 p-[4px] rounded-md border border-gray-400 bg-white data-checked:bg-primary-500 ring:0 active:ring-primary-300 focus:ring-primary-500 cursor-pointer',
              isFocused && 'border-primary-400'
            )}
          >
            <CheckIcon className="text-white stroke-2 size-2.5 opacity-0 group-data-checked:opacity-100 transition duration-200 ease-in-out" />
          </Checkbox>
        </div>
        <Typography
          variant="p3-reg"
          className={clsx('text-primary-900 cursor-pointer', isSelected ? 'font-500' : 'font-400')}
        >
          {option.name}
        </Typography>
      </div>
    ),
    [handleSelect]
  );

  return (
    <div className="flex w-full flex-1 flex-col">
      <SearchFilterBase
        inputValue={inputValue}
        onInputChange={handleInputChange}
        inputRef={inputRef}
        placeholder="Search"
        searchResults={searchResults}
        isLoading={isLoading}
        showResults={showResults}
        focusedIndex={focusedIndex}
        flattenedResults={flattenedResults}
        localSelections={localSelections}
        onSelect={handleSelect}
        selectedIdSet={selectedIdSet}
        renderOption={renderOption}
        className="w-full"
        mustTypeFirstMessage="Start typing volume id"
        resultsRef={resultsRef}
        onResultsMouseDown={handleResultsMouseDown}
        onResultsMouseUp={handleResultsMouseUp}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />
    </div>
  );
}
